import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaMoneyBillTransfer } from 'react-icons/fa6';
import { transactionsService } from '../services';
import type { Transaction } from '../types';
import { Card, EmptyState, LoaderComponent } from './ui';
import { todayCv } from '../utils/tempoCv';

type FilterPeriod = 'month' | 'week' | 'today';
type StatusFilter = 'all' | 'paid' | 'pending' | 'failed';

interface RecentTransactionsCardProps {
  limit?: number;
  showFilters?: boolean;
}

const isPaid = (estado: string) => {
  const s = (estado || '').toUpperCase();
  // NPAGO («não pago») também contém «PAGO» — não pode passar por pago.
  if (s === 'NPAGO' || s.startsWith('NÃO') || s.startsWith('NAO')) return false;
  return ['PAGO', 'PG', 'PAID', 'CONCLUÍDO', 'CONCLUIDO'].includes(s) || s.includes('PAGO');
};

const isAnulada = (estado: string) => ['ANUL', 'ANULADA', 'ANULADO'].includes((estado || '').toUpperCase());

const isPending = (estado: string) => {
  const s = (estado || '').toUpperCase();
  return ['PENDENTE', 'PENDING', 'NPAGO', 'AGUARDANDO', 'EMITIDO', 'RASC'].includes(s) || s.includes('PEND');
};

const RecentTransactionsCard: React.FC<RecentTransactionsCardProps> = ({
  limit = 10,
  showFilters = true,
}) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterPeriod>('month');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('paid');

  const getDateRange = useCallback((period: FilterPeriod): { startDate: string; endDate: string } => {
    // Dias civis de CV (não do browser nem de UTC), aritmética em UTC sobre o dia de hoje em CV.
    const [y, m, d] = todayCv().split('-').map(Number);
    const dia = (dDias: number, dMeses = 0) =>
      new Date(Date.UTC(y, m - 1 + dMeses, d + dDias)).toISOString().split('T')[0];
    let startDate = dia(0);
    switch (period) {
      case 'today': startDate = dia(0); break;
      case 'week': startDate = dia(-7); break;
      case 'month': startDate = dia(0, -1); break;
    }
    return {
      startDate,
      endDate: dia(1),
    };
  }, []);

  const loadTransactions = useCallback(async (period: FilterPeriod) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(period);
      const data = await transactionsService.getFilteredTransactions(startDate, endDate, limit);
      setTransactions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, limit]);

  useEffect(() => {
    loadTransactions(activeFilter);
  }, [activeFilter, loadTransactions]);

  const filteredTransactions = transactions.filter(tx => {
    if (statusFilter === 'all') return true;
    const estado = tx.estado || tx.estadoId || 'N';
    if (statusFilter === 'paid') return isPaid(estado);
    if (statusFilter === 'pending') return isPending(estado);
    if (statusFilter === 'failed') return !isPaid(estado) && !isPending(estado) && !isAnulada(estado);
    return true;
  });

  const formatAmount = (amount: number): string =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(amount);

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short' });

  const getStatusBadge = (estado: string): JSX.Element => {
    if (isPaid(estado)) return <span className="badge-success">Pago</span>;
    if (isPending(estado)) return <span className="badge-warning">Não Concluído</span>;
    if (isAnulada(estado)) return <span className="badge-info">Anulada</span>;
    return <span className="badge-error">Falhado</span>;
  };

  const getLogo = (tx: Transaction) => {
    const logo = (tx as any).entidadeLogo || tx.logo;
    const mime = (tx as any).entidadeMime || tx.mimetype || 'image/png';
    if (!logo) return null;
    const src = logo.startsWith('data:') ? logo : `data:${mime};base64,${logo}`;
    return src;
  };

  const handleClick = (tx: Transaction) => {
    navigate('/transactions', { state: { openTransaction: tx.id } });
  };

  const periodButtons: { period: FilterPeriod; label: string }[] = [
    { period: 'month', label: 'Mês' },
    { period: 'week', label: 'Semana' },
    { period: 'today', label: 'Hoje' },
  ];

  const statusButtons: { status: StatusFilter; label: string }[] = [
    { status: 'paid', label: 'Pagos' },
    { status: 'pending', label: 'Não Concluído' },
    { status: 'all', label: 'Todos' },
  ];

  return (
    <Card>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-pagali-gray-800">Últimas transações</h3>
          <Link to="/transactions" className="text-sm text-pagali-primary hover:underline">Ver todas</Link>
        </div>

        {/* Filters row */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Period filters */}
            <div className="flex items-center gap-1">
              {periodButtons.map(({ period, label }) => (
                <button
                  key={period}
                  onClick={() => setActiveFilter(period)}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    activeFilter === period
                      ? 'bg-pagali-purple text-white'
                      : 'bg-pagali-gray-100 text-pagali-gray-600 hover:bg-pagali-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="w-px h-5 bg-pagali-gray-200" />

            {/* Status filters */}
            <div className="flex items-center gap-1">
              {statusButtons.map(({ status, label }) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    statusFilter === status
                      ? 'bg-pagali-purple text-white'
                      : 'bg-pagali-gray-100 text-pagali-gray-600 hover:bg-pagali-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <LoaderComponent size="sm" />
      ) : error ? (
        <p className="text-pagali-error text-sm py-4 text-center">{error}</p>
      ) : filteredTransactions.length === 0 ? (
        <EmptyState
          icon={<FaMoneyBillTransfer className="text-4xl" />}
          title="Nenhuma transação"
          description="Suas transações mais recentes aparecerão aqui."
        />
      ) : (
        <div className="space-y-1">
          {filteredTransactions.map((tx) => {
            const logoSrc = getLogo(tx);
            return (
              <div
                key={tx.id}
                onClick={() => handleClick(tx)}
                className="transaction-item p-3 rounded-xl cursor-pointer hover:bg-pagali-gray-50 transition-colors"
              >
                {/* Left - Logo & Info */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt={tx.entidade || ''}
                      className="w-11 h-11 rounded-xl object-cover flex-shrink-0 bg-pagali-gray-50"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-xl bg-pagali-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-pagali-gray-500 text-sm font-bold">
                        {(tx.entidadeNome || tx.entidade || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-pagali-gray-800 truncate">
                      {tx.entidadeNome || tx.entidade || tx.servico || 'Pagamento'}
                    </p>
                    <p className="text-xs text-pagali-gray-500">
                      {formatDate(tx.data)}
                    </p>
                  </div>
                </div>

                {/* Right - Amount & Status */}
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="font-semibold text-pagali-gray-800">
                    {formatAmount(tx.valor || tx.montante || 0)}
                  </p>
                  {getStatusBadge(tx.estado || tx.estadoId || 'N')}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default RecentTransactionsCard;
